import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Zap, Sparkles, Crown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_INFO = {
  basic: { name: "Basic", price: 950, priceIva: "1.149,50", icon: Zap, color: "bg-black" },
  pro: { name: "Pro", price: 1450, priceIva: "1.754,50", icon: Sparkles, color: "bg-gradient-to-br from-purple-500 to-pink-500" },
  enterprise: { name: "Enterprise", price: 1950, priceIva: "2.359,50", icon: Crown, color: "bg-black" },
};

export default function Register() {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const planData = selectedPlan ? PLAN_INFO[selectedPlan] : null;
  const PlanIcon = planData?.icon;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    storeName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (formData.password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    setLoading(true);
    try {
      const res = await registerUser(formData.email, formData.password, formData.storeName);
      
      if (selectedPlan && PLAN_INFO[selectedPlan]) {
        // User selected a plan from landing — redirect to Stripe Checkout with 14-day trial
        toast.success("Cuenta creada. Redirigiendo a la pasarela de pago...");
        const token = localStorage.getItem("token");
        const checkoutRes = await axios.post(
          `${API}/subscription/checkout`,
          { 
            plan_type: selectedPlan,
            origin_url: window.location.origin,
            trial_days: 14
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (checkoutRes.data.checkout_url) {
          window.location.href = checkoutRes.data.checkout_url;
          return;
        }
      }
      
      toast.success("Cuenta creada exitosamente!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Back button */}
      <div className="p-4 absolute top-0 left-0 z-50">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/landing")}
          className="gap-2 text-white bg-black/40 hover:bg-black/60 backdrop-blur-sm"
          data-testid="back-to-landing"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al inicio
        </Button>
      </div>
      
      <div className="flex-1 flex">
        {/* Left - Gradient panel */}
        <div 
          className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)' }}
        >
          <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full pt-24">
            <div>
              <img src="/logo-white.png" alt="SkiFlow Rental" className="h-16 w-auto object-contain mb-8" />
              <h1 className="text-5xl font-black mb-4 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Despacha en<br/>
                <span className="italic">segundos</span>,<br/>
                no en minutos.
              </h1>
              <p className="text-xl text-white/90 leading-relaxed max-w-md">
                El software de alquiler que elimina las colas y recupera el control de tu stock en tiempo real.
              </p>
            </div>
            
            {/* Selected plan info or default features */}
            {planData ? (
              <div className="space-y-4">
                <div className="p-5 bg-white/15 backdrop-blur-md rounded-2xl border border-white/20">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`h-10 w-10 rounded-xl ${planData.color} flex items-center justify-center`}>
                      {PlanIcon && <PlanIcon className="h-5 w-5 text-white" />}
                    </div>
                    <div>
                      <p className="font-bold text-lg">Plan {planData.name}</p>
                      <p className="text-white/70 text-sm">{planData.priceIva} EUR/ano (IVA incl.)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Badge className="bg-emerald-500/30 text-emerald-100 border-emerald-400/30 text-xs">14 dias gratis</Badge>
                    <span className="text-white/60 text-xs">No se cobra hasta que termine el periodo de prueba</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Todas las funciones desde el dia 1</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Cancela cuando quieras</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">14 días de prueba gratis</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Sin tarjeta de credito</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Cancela cuando quieras</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right - Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-4 lg:hidden">
                <img src="/logo.png" alt="SkiFlow Rental" className="h-12 w-auto object-contain" />
              </div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {planData ? `Contratar Plan ${planData.name}` : "Crear Cuenta Gratis"}
              </CardTitle>
              <CardDescription>
                {planData 
                  ? "Crea tu cuenta y configura el pago. No se cobra nada durante los primeros 14 dias."
                  : "Empieza tu prueba gratis de 14 días. Sin tarjeta de crédito."
                }
              </CardDescription>
              
              {/* Mobile plan badge */}
              {planData && (
                <div className="flex justify-center pt-2 lg:hidden">
                  <Badge className="bg-purple-100 text-purple-800 font-semibold px-4 py-1.5" data-testid="mobile-plan-badge">
                    Plan {planData.name} - 14 dias gratis
                  </Badge>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="storeName">Nombre de tu Tienda *</Label>
                  <Input
                    id="storeName"
                    name="storeName"
                    type="text"
                    placeholder="Ej: Ski Rental Madrid"
                    value={formData.storeName}
                    onChange={handleChange}
                    className="h-11"
                    required
                    disabled={loading}
                    data-testid="register-store-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="h-11"
                    required
                    disabled={loading}
                    data-testid="register-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Al menos 8 caracteres"
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11"
                    required
                    disabled={loading}
                    minLength={8}
                    data-testid="register-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Contraseña *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Repite tu contraseña"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="h-11"
                    required
                    disabled={loading}
                    minLength={8}
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-11 font-semibold bg-black hover:bg-black/90 text-white" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creando cuenta...</>
                  ) : planData ? (
                    "Crear Cuenta y Configurar Pago"
                  ) : (
                    "Crear Cuenta Gratis"
                  )}
                </Button>

                {planData && (
                  <p className="text-xs text-center text-slate-500 bg-slate-50 rounded-lg p-3" data-testid="trial-info-text">
                    Al completar el registro, seras redirigido a Stripe para introducir tus datos de pago. 
                    <strong> No se realizara ningun cobro hasta pasados los 14 dias de prueba.</strong>
                  </p>
                )}

                <p className="text-sm text-center text-slate-600">
                  Ya tienes cuenta?{" "}
                  <Link to="/login" className="text-black hover:underline font-medium">
                    Iniciar Sesion
                  </Link>
                </p>

                <p className="text-xs text-center text-slate-500 mt-4">
                  Al crear una cuenta, aceptas nuestros{" "}
                  <a href="#" className="text-black hover:underline">Terminos de Servicio</a>
                  {" "}y{" "}
                  <a href="#" className="text-black hover:underline">Politica de Privacidad</a>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
